import { useState, useCallback, useEffect } from "react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Download, FileText, Printer, Mail } from "lucide-react";
import { toast } from "sonner";
import { RoomSalesTable, ArticleSalesTable, UnpostedTable, GratuityTable, GenericTable } from "./ReportTables";

const REPORT_TYPES = [
  { value: "room-sales", label: "Room Sales Report" },
  { value: "article-sales", label: "Article Sales Report" },
  { value: "gratuity", label: "Gratuity Report" },
  { value: "unposted", label: "Unposted Transactions" },
  { value: "daily-sales", label: "Daily Sales Summary" },
  { value: "shift-report", label: "Shift Report" },
  { value: "tax-collection", label: "Tax Collection Report" },
  { value: "void-report", label: "Void Report" },
];

const QUICK_DATES = [
  ['today', 'Today'], ['yesterday', 'Yesterday'],
  ['7days', '7 Days'], ['30days', '30 Days'], ['lastmonth', 'Last Month'],
];

function getQuickDates(label) {
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  if (label === 'today') return { from: fmt(today), to: fmt(today) };
  if (label === 'yesterday') { const y = new Date(today); y.setDate(y.getDate() - 1); return { from: fmt(y), to: fmt(y) }; }
  if (label === '7days') { const d = new Date(today); d.setDate(d.getDate() - 6); return { from: fmt(d), to: fmt(today) }; }
  if (label === '30days') { const d = new Date(today); d.setDate(d.getDate() - 29); return { from: fmt(d), to: fmt(today) }; }
  const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const last = new Date(today.getFullYear(), today.getMonth(), 0);
  return { from: fmt(first), to: fmt(last) };
}

export default function Reports() {
  const { adminUser } = useAuth();
  const [reportType, setReportType] = useState("room-sales");
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [filterRoom, setFilterRoom] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterOperator, setFilterOperator] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [operators, setOperators] = useState([]);
  const [emailModal, setEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    api.get("/admin/users").then(r => setOperators(r.data.filter(u => u.role === 'pos_operator'))).catch(() => {});
  }, []);

  const generate = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (filterRoom) params.room = filterRoom;
      if (filterStatus) params.status = filterStatus;
      if (filterOperator) params.operator = filterOperator;
      const res = await api.get(`/admin/reports/${reportType}`, { params });
      setData(res.data);
    } catch { toast.error("Failed to generate report"); }
    finally { setLoading(false); }
  }, [reportType, dateFrom, dateTo, filterRoom, filterStatus, filterOperator]);

  const exportCSV = () => {
    if (!data) return;
    let rows = [];
    if (reportType === 'room-sales' && data.rooms) {
      rows = data.rooms.flatMap(r => r.lines.map(l => ({ Room: r.room_number, Guest: r.guest_name, ...l })));
    } else if (reportType === 'article-sales' && data.sections) {
      rows = data.sections.flatMap(s => s.items.map(i => ({ Category: s.category, ...i })));
    } else if (reportType === 'unposted' && data.transactions) {
      rows = data.transactions.map(t => ({ Ref: t.transaction_ref, Room: t.room_number, Guest: t.guest_name, Operator: t.operator_name, Total: t.grand_total, Status: t.status, Error: t.pms_error || '', Date: t.created_at?.slice(0, 10) }));
    } else if (reportType === 'gratuity' && data.data) {
      rows = data.data;
    } else if (data.data) {
      rows = data.data;
    }
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const csv = [headers.join(","), ...rows.map(r => headers.map(h => {
      let v = String(r[h] ?? "");
      if (/^[=+\-@]/.test(v)) v = "'" + v;
      if (v.includes(",")) v = `"${v}"`;
      return v;
    }).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `${reportType}_${dateFrom}_${dateTo}.csv`;
    a.click();
  };

  const emailReport = async () => {
    setEmailing(true);
    try {
      const res = await api.post("/admin/reports/email", { report_type: reportType, date_from: dateFrom, date_to: dateTo, recipient: emailTo });
      toast.success(res.data.message);
      setEmailModal(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send email"); }
    finally { setEmailing(false); }
  };

  const setQuickDate = (label) => { const { from, to } = getQuickDates(label); setDateFrom(from); setDateTo(to); };

  const showRoomFilter = reportType === 'room-sales';
  const showStatusFilter = reportType === 'room-sales';
  const showOperatorFilter = reportType === 'room-sales' || reportType === 'shift-report';

  const renderReport = () => {
    if (!data) return null;
    switch (reportType) {
      case 'room-sales': return <RoomSalesTable data={data} />;
      case 'article-sales': return <ArticleSalesTable data={data} />;
      case 'unposted': return <UnpostedTable data={data} />;
      case 'gratuity': return <GratuityTable data={data} />;
      default: return <GenericTable data={data} />;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Reports</h1>

      <div className="flex flex-wrap items-end gap-3 bg-white border border-gray-200 p-4 print:hidden">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">Report</Label>
          <Select value={reportType} onValueChange={setReportType}>
            <SelectTrigger className="w-56 rounded-none h-9 text-sm mt-1" data-testid="report-type-select"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              {REPORT_TYPES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-none h-9 text-sm mt-1" data-testid="report-date-from" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-none h-9 text-sm mt-1" data-testid="report-date-to" />
        </div>
        <div className="flex flex-wrap gap-1 items-end">
          {QUICK_DATES.map(([k, label]) => (
            <button key={k} onClick={() => setQuickDate(k)}
              className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-gray-200 bg-white hover:bg-black hover:text-white transition-colors"
              data-testid={`quick-date-${k}`}>{label}</button>
          ))}
        </div>
        {showRoomFilter && (
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">Room</Label>
            <Input value={filterRoom} onChange={(e) => setFilterRoom(e.target.value)} placeholder="All" className="w-20 rounded-none h-9 text-sm mt-1" />
          </div>
        )}
        {showStatusFilter && (
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">Status</Label>
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-32 rounded-none h-9 text-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="posted">Posted</SelectItem>
                <SelectItem value="local_only">Local Only</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {showOperatorFilter && (
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">Operator</Label>
            <Select value={filterOperator || "all"} onValueChange={(v) => setFilterOperator(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 rounded-none h-9 text-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All</SelectItem>
                {operators.map(o => <SelectItem key={o.id} value={o.id}>{o.display_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button onClick={generate} disabled={loading} className="rounded-none bg-black text-white h-9 text-xs" data-testid="generate-report-button">
          <FileText className="w-3 h-3 mr-1" /> {loading ? "..." : "Generate"}
        </Button>
        {data && (
          <>
            <Button onClick={exportCSV} variant="outline" className="rounded-none h-9 text-xs" data-testid="export-csv-button">
              <Download className="w-3 h-3 mr-1" /> CSV
            </Button>
            <Button onClick={() => window.print()} variant="outline" className="rounded-none h-9 text-xs">
              <Printer className="w-3 h-3 mr-1" /> Print
            </Button>
            <Button onClick={() => { setEmailTo(adminUser?.email || ''); setEmailModal(true); }} variant="outline" className="rounded-none h-9 text-xs" data-testid="email-report-button">
              <Mail className="w-3 h-3 mr-1" /> Email
            </Button>
          </>
        )}
      </div>

      {data && (
        <div className="bg-white border border-gray-200 p-4 overflow-x-auto">
          {renderReport()}
        </div>
      )}

      <Dialog open={emailModal} onOpenChange={setEmailModal}>
        <DialogContent className="rounded-none border-gray-200 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Email Report</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Send <strong>{REPORT_TYPES.find(r => r.value === reportType)?.label}</strong> ({dateFrom} to {dateTo}) as PDF attachment.
            </p>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Recipient Email</Label>
              <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="email@example.com" type="email"
                className="mt-1 rounded-none" data-testid="email-recipient-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailModal(false)} className="rounded-none">Cancel</Button>
            <Button onClick={emailReport} disabled={emailing || !emailTo} className="rounded-none bg-black text-white hover:bg-gray-800" data-testid="send-email-button">
              <Mail className="w-3 h-3 mr-1" /> {emailing ? "Sending..." : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
